import React from 'react'
import { isEmpty, isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import Select from '@/components/forms/Select'

import { ALL_PLAYBOOKS, DELETED_PLAYBOOK, getDeletedPlaybookCids } from './AiAnswers.helpers'

// An answer limited only to deleted playbooks serves no one until a playbook is chosen.
const deletedWarning = (deletedCount, scopeCount) => {
  if (deletedCount === scopeCount) {
    return deletedCount === 1
      ? "This answer's playbook was deleted, so AgentGPT isn't using it. Choose a playbook to use it again."
      : "This answer's playbooks were deleted, so AgentGPT isn't using it. Choose a playbook to use it again."
  }

  return deletedCount === 1
    ? "One of this answer's playbooks was deleted. AgentGPT still uses it for the others."
    : `${deletedCount} of this answer's playbooks were deleted. AgentGPT still uses it for the others.`
}

/**
 * Which playbooks a verified answer is served for; empty serves every playbook,
 * matching a SharePoint or Google Drive row left unscoped.
 *
 * @import import { AiAnswersPlaybookField } from '@/views/AiAnswers/AiAnswersPlaybookField'
 * @param {object} props
 * @param {{options: object[], loading: boolean, failed: boolean, retry: Function}} props.playbooks
 *   The org's playbooks as { value: cid, label: name } options, their load state, and a refetch
 * @param {string[]} props.value The selected playbook cids
 * @param {Function} props.onChange Called with the new list of cids
 */
export const AiAnswersPlaybookField = ({ playbooks, value, onChange }) => {
  // A deleted playbook shows as a selection the manager can see and remove, never as a
  // hidden restriction behind "All playbooks".
  const deletedCids = getDeletedPlaybookCids(value, playbooks)
  const options = [
    ...playbooks.options,
    ...deletedCids.map((cid) => ({ value: cid, label: DELETED_PLAYBOOK })),
  ]

  return (
    <>
      <FormField label="Playbooks">
        <Select
          isMulti
          aria-label="Playbooks"
          options={options}
          value={options.filter((option) => value.includes(option.value))}
          loading={playbooks.loading}
          isDisabled={playbooks.loading || playbooks.failed}
          placeholder={playbooks.failed ? 'Playbooks unavailable' : ALL_PLAYBOOKS}
          onChange={(selected) =>
            onChange(isNil(selected) ? [] : selected.map((option) => option.value))
          }
          data-testid="answer-playbooks"
        />
      </FormField>
      {playbooks.failed && (
        <div className="flex-align-center small-gap">
          <ErrorMessage warning content="Playbooks couldn't be loaded." />
          <Button small onClick={playbooks.retry}>
            Retry
          </Button>
        </div>
      )}
      {!isEmpty(deletedCids) && (
        <ErrorMessage warning content={deletedWarning(deletedCids.length, value.length)} />
      )}
    </>
  )
}
